import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useSearch, Link } from 'wouter';

import { POST_LOGIN_RETURN_KEY } from '@/lib/authUtils';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import kalovateLogoLight from '@/assets/brand/kalovate-stacked-full-color.svg';
import kalovateLogoDark from '@/assets/brand/kalovate-stacked-reversed.svg';

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const passwordResetSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type SignupForm = z.infer<typeof signupSchema>;
type LoginForm = z.infer<typeof loginSchema>;
type PasswordResetForm = z.infer<typeof passwordResetSchema>;

function resolveReturnTo(value: string): string {
  if (!value) return '/';
  try {
    const decoded = decodeURIComponent(value);
    if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded === '/login') {
      return '/';
    }
    return decoded;
  } catch {
    return '/';
  }
}

export default function Login() {
  // Temporarily disable Google auth UI until configured
  const ENABLE_GOOGLE_AUTH = false;
  const [isSignup, setIsSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const searchString = useSearch();
  const returnTo = new URLSearchParams(searchString).get('returnTo') ?? '';

  const signupForm = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
    },
  });

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const passwordResetForm = useForm<PasswordResetForm>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      email: '',
    },
  });

  // Redirect if already authenticated
  if (!authLoading && isAuthenticated) {
    navigate(resolveReturnTo(returnTo));
    return null;
  }

  const handleSignup = async (data: SignupForm) => {
    setIsLoading(true);
    try {
      await apiRequest('POST', '/api/auth/register', data);
    } catch (error: any) {
      toast({
        title: 'Sign Up Failed',
        description: error.message || 'Failed to create account',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    // Account created — auto-login so the user doesn't need to re-enter credentials
    try {
      await apiRequest('POST', '/api/auth/login', { email: data.email, password: data.password });
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({ title: 'Welcome to Kalovate!', description: 'Your account has been created.' });
      signupForm.reset();
      navigate(resolveReturnTo(returnTo));
    } catch {
      // Account was created but login failed — ask the user to log in manually
      toast({
        title: 'Account Created',
        description: 'Your account is ready. Please log in to continue.',
      });
      setIsSignup(false);
      loginForm.setValue('email', data.email);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await apiRequest('POST', '/api/auth/login', data);
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({
        title: 'Welcome Back',
        description: 'You have been logged in successfully.',
      });
      navigate(resolveReturnTo(returnTo));
    } catch (error: any) {
      toast({
        title: 'Login Failed',
        description: error.message || 'Invalid email or password',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider: string) => {
    if (provider === 'google') {
      if (returnTo) {
        window.sessionStorage.setItem(POST_LOGIN_RETURN_KEY, resolveReturnTo(returnTo));
      }
      window.location.href = '/api/auth/google';
    } else {
      console.error('Unsupported authentication provider:', provider);
    }
  };

  const handlePasswordReset = async (data: PasswordResetForm) => {
    setIsResetLoading(true);
    try {
      await apiRequest('POST', '/api/auth/request-reset', data);
      toast({
        title: 'Reset Link Sent',
        description: "If an account with that email exists, we've sent you a password reset link.",
      });
      setIsPasswordResetOpen(false);
      passwordResetForm.reset();
    } catch (error: any) {
      toast({
        title: 'Reset Failed',
        description: error.message || 'Failed to send reset email',
        variant: 'destructive',
      });
    } finally {
      setIsResetLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4">
      <Link href="/" className="mb-6 inline-flex">
        <img src={kalovateLogoLight} alt="Kalovate" className="h-16 w-auto dark:hidden" />
        <img src={kalovateLogoDark} alt="Kalovate" className="hidden h-16 w-auto dark:block" />
      </Link>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </CardTitle>
          <CardDescription className="text-center">
            {isSignup
              ? 'Join Kalovate to start learning from expert creators'
              : 'Sign in to your account to continue learning'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {ENABLE_GOOGLE_AUTH && (
            <>
              {/* Social Login Buttons */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleSocialLogin('google')}
                  data-testid="button-google-login"
                >
                  {/* Google icon placeholder when enabled */}
                  <span className="w-4 h-4 mr-2">G</span>
                  Continue with Google
                </Button>
              </div>

              <div className="relative">
                <Separator />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-background px-2 text-muted-foreground text-sm">or</span>
                </div>
              </div>
            </>
          )}

          {/* Email Form */}
          {isSignup ? (
            <Form key="signup" {...signupForm}>
              <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={signupForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} data-testid="input-first-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={signupForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} data-testid="input-last-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={signupForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="john@example.com"
                          type="email"
                          {...field}
                          data-testid="input-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signupForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="••••••••"
                            type={showSignupPassword ? 'text' : 'password'}
                            {...field}
                            data-testid="input-password"
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSignupPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
                          >
                            {showSignupPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full py-5 text-base"
                  disabled={isLoading}
                  data-testid="button-signup"
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </Form>
          ) : (
            <Form key="login" {...loginForm}>
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <FormField
                  control={loginForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="john@example.com"
                          type="email"
                          {...field}
                          data-testid="input-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={loginForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="••••••••"
                            type={showLoginPassword ? 'text' : 'password'}
                            {...field}
                            data-testid="input-password"
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowLoginPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                          >
                            {showLoginPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full py-5 text-base"
                  disabled={isLoading}
                  data-testid="button-login"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>

                {/* Forgot Password Link */}
                <div className="text-center">
                  <Dialog open={isPasswordResetOpen} onOpenChange={setIsPasswordResetOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="link"
                        className="text-sm text-muted-foreground hover:text-primary p-0"
                        data-testid="button-forgot-password"
                      >
                        Forgot your password?
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Reset Your Password</DialogTitle>
                        <DialogDescription>
                          Enter your email address and we'll send you a link to reset your password.
                        </DialogDescription>
                      </DialogHeader>
                      <Form {...passwordResetForm}>
                        <form
                          onSubmit={passwordResetForm.handleSubmit(handlePasswordReset)}
                          className="space-y-4"
                        >
                          <FormField
                            control={passwordResetForm.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email Address</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="john@example.com"
                                    type="email"
                                    {...field}
                                    data-testid="input-reset-email"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <DialogFooter className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setIsPasswordResetOpen(false)}
                              data-testid="button-cancel-reset"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              disabled={isResetLoading}
                              data-testid="button-send-reset"
                            >
                              {isResetLoading ? 'Sending...' : 'Send Reset Link'}
                            </Button>
                          </DialogFooter>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>
                </div>
              </form>
            </Form>
          )}

          <div className="flex items-center gap-2 my-1">
            <span className="flex-1 border-t border-border/50" />
            <span className="text-xs uppercase text-muted-foreground px-1">
              {isSignup ? 'Already have an account?' : 'New to Kalovate?'}
            </span>
            <span className="flex-1 border-t border-border/50" />
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setIsSignup(!isSignup)}
            data-testid="button-toggle-mode"
          >
            {isSignup ? 'Sign in instead' : 'Create a free account'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
